import {
  buildRecentQuarterPresetOptions,
  formatQuarterLabel,
  nowToronto,
  quarterStartFromIso,
  toIsoDate,
} from "@/analytics/core/period-windows";
import {
  PERIOD_PRESET_LABELS,
  type PeriodGrain,
  type PeriodPreset,
  type PeriodSpec,
} from "@/analytics/core/types";
import { Input } from "@/components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/cn";
import { startOfQuarter, subQuarters } from "date-fns";
import type { ReactNode } from "react";

const PRESETS = Object.keys(PERIOD_PRESET_LABELS) as PeriodPreset[];

const GRAIN_OPTIONS: { value: PeriodGrain; label: string }[] = [
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
];

const CUSTOM_SELECT_VALUE = "custom";
const QUARTER_PRESET_PREFIX = "quarter:";

/** How many past quarters to offer in the custom quarter picker (plus current). */
const QUARTER_LOOKBACK = 11;

function defaultCustomStart(): string {
  return toIsoDate(nowToronto());
}

function buildQuarterOptions(asOf = nowToronto()): { value: string; label: string }[] {
  const current = startOfQuarter(asOf);
  const options: { value: string; label: string }[] = [];
  for (let i = 0; i <= QUARTER_LOOKBACK; i += 1) {
    const start = toIsoDate(subQuarters(current, i));
    options.push({ value: start, label: formatQuarterLabel(start) });
  }
  return options;
}

function isNamedQuarterSpec(spec: PeriodSpec): boolean {
  return spec.preset === "custom" && spec.grain === "quarter" && Boolean(spec.customStart);
}

function quarterPresetValue(quarterStart: string): string {
  return `${QUARTER_PRESET_PREFIX}${quarterStart}`;
}

function resolveSelectValue(periodSpec: PeriodSpec): string {
  if (isNamedQuarterSpec(periodSpec)) {
    return quarterPresetValue(quarterStartFromIso(periodSpec.customStart!));
  }
  if (periodSpec.preset === "custom") return CUSTOM_SELECT_VALUE;
  return periodSpec.preset;
}

function resolveSelectLabel(periodSpec: PeriodSpec): string {
  if (isNamedQuarterSpec(periodSpec)) {
    return formatQuarterLabel(quarterStartFromIso(periodSpec.customStart!));
  }
  if (periodSpec.preset === "custom") return "Custom period";
  return PERIOD_PRESET_LABELS[periodSpec.preset as PeriodPreset];
}

interface PeriodSelectProps {
  periodSpec: PeriodSpec;
  onChange: (spec: PeriodSpec) => void;
  /** Renders after the period controls (e.g. compare mode). */
  trailing?: ReactNode;
  className?: string;
}

export function PeriodSelect({ periodSpec, onChange, trailing, className }: PeriodSelectProps) {
  const selectValue = resolveSelectValue(periodSpec);
  const isAdvancedCustom = periodSpec.preset === "custom" && selectValue === CUSTOM_SELECT_VALUE;
  const grain = periodSpec.grain ?? "week";
  const customStart = periodSpec.customStart ?? defaultCustomStart();
  const recentQuarterPresets = buildRecentQuarterPresetOptions();

  const handlePresetChange = (next: string) => {
    if (next.startsWith(QUARTER_PRESET_PREFIX)) {
      const quarterStart = next.slice(QUARTER_PRESET_PREFIX.length);
      onChange({ preset: "custom", grain: "quarter", customStart: quarterStart });
      return;
    }
    if (next === CUSTOM_SELECT_VALUE) {
      onChange({ preset: "custom", grain: "week", customStart: defaultCustomStart() });
      return;
    }
    onChange({ preset: next as PeriodPreset });
  };

  const quarterOptions = buildQuarterOptions();
  const quarterValue = quarterStartFromIso(customStart);
  const quarterInList = quarterOptions.some((option) => option.value === quarterValue);
  const quarterSelectOptions = quarterInList
    ? quarterOptions
    : [{ value: quarterValue, label: formatQuarterLabel(quarterValue) }, ...quarterOptions];

  const handleGrainChange = (next: string) => {
    const nextGrain = next as PeriodGrain;
    let nextStart = customStart;
    if (nextGrain === "month") {
      nextStart = `${customStart.slice(0, 7)}-01`;
    } else if (nextGrain === "quarter") {
      nextStart = quarterStartFromIso(customStart);
    }
    onChange({ preset: "custom", grain: nextGrain, customStart: nextStart });
  };

  const handleStartChange = (next: string) => {
    if (!next) return;
    if (grain === "month") {
      onChange({ preset: "custom", grain, customStart: `${next}-01` });
      return;
    }
    if (grain === "quarter") {
      onChange({ preset: "custom", grain, customStart: quarterStartFromIso(next) });
      return;
    }
    onChange({ preset: "custom", grain, customStart: next });
  };

  return (
    <div className={cn("flex shrink-0 flex-col items-end gap-2", className)}>
      <div className="flex flex-nowrap items-center justify-end gap-2">
        <Select value={selectValue} onValueChange={handlePresetChange}>
          <SelectTrigger className="min-w-40" aria-label="Period">
            <SelectValue>{resolveSelectLabel(periodSpec)}</SelectValue>
          </SelectTrigger>
          <SelectContent className="max-h-[26rem]">
            {PRESETS.map((preset) => (
              <SelectItem key={preset} value={preset}>
                {PERIOD_PRESET_LABELS[preset]}
              </SelectItem>
            ))}
            {recentQuarterPresets.map((option) => (
              <SelectItem key={option.quarterStart} value={quarterPresetValue(option.quarterStart)}>
                {option.label}
              </SelectItem>
            ))}
            <SelectItem value={CUSTOM_SELECT_VALUE}>Custom period…</SelectItem>
          </SelectContent>
        </Select>
        {trailing}
      </div>

      {isAdvancedCustom ? (
        <div className="flex flex-nowrap items-center justify-end gap-2">
          <Select value={grain} onValueChange={handleGrainChange}>
            <SelectTrigger className="w-28" aria-label="Period type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GRAIN_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {grain === "week" ? (
            <div className="w-40 shrink-0">
              <Input
                type="date"
                value={customStart}
                onChange={(event) => handleStartChange(event.target.value)}
                aria-label="Day in week"
                title="Any day in the week"
              />
            </div>
          ) : null}

          {grain === "month" ? (
            <div className="w-40 shrink-0">
              <Input
                type="month"
                value={customStart.slice(0, 7)}
                onChange={(event) => handleStartChange(event.target.value)}
                aria-label="Month"
              />
            </div>
          ) : null}

          {grain === "quarter" ? (
            <Select value={quarterValue} onValueChange={handleStartChange}>
              <SelectTrigger className="min-w-28" aria-label="Quarter">
                <SelectValue>{formatQuarterLabel(quarterValue)}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {quarterSelectOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
